// Copied verbatim from eval/dashboard/internal_web/src/components/session/useAnchoredTurn.ts (ATM-57). Keep in step until ATM-60 shares it.
/**
 * Which exchange the differential panel is showing, driven by the scroll position.
 *
 * The panel sits sticky beside a long transcript, so the question it answers — "what did
 * the model believe *at this point*?" — is only useful if "this point" is the part of the
 * conversation being read. Stepping it by hand meant a reviewer scrolled with one hand and
 * clicked with the other to keep the two in step, and the panel was silently about a
 * different turn the moment they stopped clicking.
 *
 * **The scroll position decides, with one exception.** An observer watches a thin reading band
 * about a third of the way down the viewport (a negative `rootMargin`): the last exchange to
 * enter it is the one being read, one that drops out below it hands back to the exchange
 * before, and between exchanges the previous one is kept, so the panel never blanks mid-scroll.
 * The exception is a step click: it scrolls the target's top into the band and anchors the
 * target at once, and the observer ignores the scroll that click started — until `scrollend`,
 * with a timeout for browsers that never send one — so the exchanges it sweeps past cannot
 * flash through the panel on the way.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import { scrollBehavior } from "../../lib/motion";

/** Marks a turn as observable. Read by the hook, written by `Transcript`. */
export const AGENT_TURN_ATTR = "data-agent-turn";

/**
 * The reading band: from 32% down the viewport to 42% down.
 *
 * Top and bottom are trimmed by `rootMargin`, which takes them as insets, so these are
 * "100% minus the strip" on each side.
 */
const BAND_TOP_PCT = 32;
const BAND_BOTTOM_PCT = 58;
/** Where a stepped-to exchange's top lands: the middle of the band, so it is inside it. */
const READ_LINE_PCT = (BAND_TOP_PCT + (100 - BAND_BOTTOM_PCT)) / 2;

type Anchored = {
  /** The turn index the panel should show, or `null` while nothing has been resolved. */
  anchoredIndex: number | null;
  /** Scroll one exchange's top into the reading band, and anchor it. */
  scrollToTurn: (turnIndex: number) => void;
};

/**
 * The exchange to anchor for a turn: the last anchorable one at or before it, else the first.
 * So a jump to a question med-reasoning answered itself shows the answer standing before it.
 */
export function anchorFor(turnIndex: number, anchorable: readonly number[]): number | null {
  const before = anchorable.filter((index) => index <= turnIndex);
  return before.at(-1) ?? anchorable[0] ?? null;
}

/**
 * Track which exchange is being read inside `containerRef`.
 *
 * Only `anchorable` exchanges are observed — the ones the stepper can land on — so scrolling
 * past a question med-reasoning answered itself keeps the answer before it, exactly as the
 * arrows skip it. Every exchange can still be scrolled to. `anchorable` must be memoised by the
 * caller: the observer is rebuilt whenever it changes.
 */
export function useAnchoredTurn(
  containerRef: RefObject<HTMLElement>,
  anchorable: readonly number[],
): Anchored {
  const [anchoredIndex, setAnchoredIndex] = useState<number | null>(null);
  // True while the scroll a step click started is still running. Not a "manual beats auto for
  // N seconds" override: it lasts exactly as long as that one scroll, so the exchanges it
  // sweeps past on the way cannot flash through the panel before the target arrives.
  const steering = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observed = new Set(anchorable);
    const nodes = Array.from(
      container.querySelectorAll<HTMLElement>(`[${AGENT_TURN_ATTR}]`),
    ).filter((node) => observed.has(Number(node.getAttribute(AGENT_TURN_ATTR))));
    if (nodes.length === 0) return;

    // Seed from the first exchange rather than leaving the panel unresolved: at the top of
    // the page none is in the band yet, and the panel must still be about something.
    const first = Number(nodes[0].getAttribute(AGENT_TURN_ATTR));
    setAnchoredIndex((held) => (held === null ? first : held));

    const observer = new IntersectionObserver(
      (entries) => {
        if (steering.current) return;
        // The LAST one in document order wins. Two short turns can share the band, and the
        // later one is the one the reader has reached.
        const inBand = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => Number(entry.target.getAttribute(AGENT_TURN_ATTR)))
          .filter((index) => Number.isFinite(index));
        if (inBand.length > 0) {
          setAnchoredIndex(Math.max(...inBand));
          return;
        }
        // Nothing entered. An exchange that left the band DOWNWARD was scrolled back past, so
        // what is being read now sits above it: step back to the anchorable exchange before
        // it, rather than keep a belief formed later than the words in the band. One that
        // left upward keeps its anchor — an earlier belief beside later words is only stale.
        const fellBelow = entries
          .filter(
            (entry) =>
              !entry.isIntersecting &&
              entry.rootBounds !== null &&
              entry.boundingClientRect.top >= entry.rootBounds.bottom,
          )
          .map((entry) => Number(entry.target.getAttribute(AGENT_TURN_ATTR)))
          .filter((index) => Number.isFinite(index));
        if (fellBelow.length === 0) return; // between exchanges: keep the last one
        const back = anchorFor(Math.min(...fellBelow) - 1, anchorable);
        if (back !== null) setAnchoredIndex(back);
      },
      {
        rootMargin: `-${BAND_TOP_PCT}% 0px -${BAND_BOTTOM_PCT}% 0px`,
        threshold: 0,
      },
    );
    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, [containerRef, anchorable]);

  const scrollToTurn = useCallback(
    (turnIndex: number) => {
      const container = containerRef.current;
      if (container === null) return;
      const node = container.querySelector<HTMLElement>(
        `[${AGENT_TURN_ATTR}="${turnIndex}"]`,
      );
      if (node === null) return;
      // The exchange's TOP goes into the reading band, not its centre. Centred, a short
      // exchange spanned 50% ± a few lines and never reached the band, so the observer kept
      // the previous anchor — and "next", computed from that anchor, scrolled to the same
      // place forever. The page scrolls the window, which is what this measures against.
      const top = node.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(0, top - (window.innerHeight * READ_LINE_PCT) / 100),
        behavior: scrollBehavior(),
      });
      // The target is known at the click, so say so at once: a scroll too small to cross the
      // band fires no observer entry at all. Every scroll after this one is the observer's.
      setAnchoredIndex(anchorFor(turnIndex, anchorable));
      steering.current = true;
      const release = () => {
        steering.current = false;
        window.removeEventListener("scrollend", release);
        window.clearTimeout(fallback);
      };
      window.addEventListener("scrollend", release);
      // `scrollend` does not fire when there was nothing to scroll, nor on older browsers.
      const fallback = window.setTimeout(release, 1000);
    },
    [containerRef, anchorable],
  );

  return { anchoredIndex, scrollToTurn };
}
